import { useRef, type ReactNode } from 'react';
import { FunnelSimpleIcon } from '@phosphor-icons/react';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import type { BusLegStepView, BusOfferView } from '../models.js';
import type { BusSeatLayout } from '../seats/bus_seat_picker.js';
import { BusDayStrip, type BusDayStripProps } from './bus_day_strip.js';
import { BusLegStepper, type BusLegStepperCopy } from './bus_leg_stepper.js';
import { BusOfferCard, type BusOfferCardCopy } from './bus_offer_card.js';
import { BUS_SEAT_SHEET_QUERY, BusSeatSheet } from './bus_seat_sheet.js';
import './bus_results_list.css';

export type BusOfferShortlistCopy = {
  /** O nome acessível da lista ("Partidas separadas para você"). */
  resultsLabel: string;
  card: BusOfferCardCopy;
  legs: BusLegStepperCopy;
  /** "Ver as 36 partidas" — abre a lista inteira. */
  seeAllLabel(total: number): string;
  /** O título da folha da escolha de poltrona no celular e no tablet. */
  seatsTitle: string;
  /** O nome acessível do voltar da folha ("Fechar escolha de poltrona"). */
  seatsCloseLabel: string;
};

export type BusOfferShortlistProps = {
  /** As poucas viagens separadas para a pessoa, na ordem do host. */
  offers: readonly BusOfferView[];
  /** Quantas viagens a busca achou — o "Ver as N partidas". */
  total: number;
  copy: BusOfferShortlistCopy;
  /** As pernas da viagem (ida, volta). Uma perna só não desenha o stepper. */
  steps?: readonly BusLegStepView[];
  onStepSelect?: (index: number) => void;
  /** A faixa de dias da perna em cena (frame 04). Ausente ⇒ sem a faixa. */
  dayStrip?: BusDayStripProps;
  selectedOfferId?: string;
  /** O card aberto na escolha de poltrona e o que abre nele — inline no card,
   *  ou na folha abaixo de `BUS_SEAT_SHEET_QUERY`. */
  expandedOfferId?: string;
  renderExpansion?: (offerId: string, layout: BusSeatLayout) => ReactNode;
  onCollapseExpansion?: () => void;
  /** Uma escrita em voo: `Selecionar` trava. */
  busy?: boolean;
  /** Sem ele, o "Ver as N partidas" não aparece. */
  onSeeAll?: () => void;
  onDetailsOpen?: (offerId: string) => void;
  onSelect(offerId: string): void;
};

/**
 * A seleção curta de viagens de ônibus da CONVERSA (frames 04–06 do Figma
 * "Conversacional → Bus"): o stepper de pernas, a faixa de dias, as poucas viagens separadas nos
 * cards da disponibilidade (os mesmos `BusOfferCard`, com selo e nota) e o "Ver
 * as N partidas" que abre a lista inteira. A escolha de poltrona abre INLINE no
 * card — ou, no celular e no tablet, em tela cheia — como na página.
 */
export function BusOfferShortlist({
  offers,
  total,
  copy,
  steps = [],
  onStepSelect,
  dayStrip,
  selectedOfferId,
  expandedOfferId,
  renderExpansion,
  onCollapseExpansion,
  busy = false,
  onSeeAll,
  onDetailsOpen,
  onSelect,
}: Readonly<BusOfferShortlistProps>): ReactNode {
  const sheet = useMediaQuery(BUS_SEAT_SHEET_QUERY);
  // Quem abriu a folha recebe o foco de volta: o `Selecionar` do card.
  const trigger = useRef<HTMLButtonElement | null>(null);
  return (
    <div className="@container/bus-shortlist grid gap-4">
      {steps.length > 1 && (
        <BusLegStepper
          steps={steps}
          copy={copy.legs}
          disabled={busy}
          {...(onStepSelect ? { onStepSelect } : {})}
        />
      )}
      {dayStrip && <BusDayStrip {...dayStrip} disabled={busy || dayStrip.disabled} />}
      <ul className="hui-bus-results-list" aria-label={copy.resultsLabel}>
        {offers.map((offer) => (
          <li key={offer.id}>
            <BusOfferCard
              offer={offer}
              copy={copy.card}
              selected={selectedOfferId === offer.id}
              busy={busy}
              {...(expandedOfferId === offer.id && renderExpansion && !sheet
                ? { expansion: renderExpansion(offer.id, 'inline') }
                : {})}
              {...(onDetailsOpen ? { onDetailsOpen } : {})}
              onSelect={(offerId) => {
                // Só um botão com foco (o `Selecionar`): um toque no iOS não foca.
                trigger.current =
                  document.activeElement instanceof HTMLButtonElement
                    ? document.activeElement
                    : null;
                onSelect(offerId);
              }}
            />
          </li>
        ))}
      </ul>
      {onSeeAll && total > offers.length && (
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onSeeAll}>
            <FunnelSimpleIcon aria-hidden="true" />
            {copy.seeAllLabel(total)}
          </Button>
        </div>
      )}
      {renderExpansion && sheet && (
        <BusSeatSheet
          offer={offers.find((candidate) => candidate.id === expandedOfferId)}
          title={copy.seatsTitle}
          closeLabel={copy.seatsCloseLabel}
          renderExpansion={renderExpansion}
          returnFocusTo={trigger}
          onClose={() => onCollapseExpansion?.()}
        />
      )}
    </div>
  );
}
